import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { auth } from "../firebaseClient";
import { listTasks, createTask, editTask, removeTask, importTasks } from "../taskStore";
import "./dash.css";
 
const TASK_NAMES = [
  "شكوى","جدول المناوبات","قاعدة البيانات","رسالة بالبروة",
  "الرد على رسالة محكمة","الرد على رسالة خارجية","متابعة موضوع",
  "الاتصال بالمريض أو المراجع","الاتصال بمندوب شركة",
  "رسالة طلب زراعة أسنان","رسالة توصية أو خبرة",
  "شهادة تقدير راتب","أخرى",
];
const STATUS_LABEL = { todo:"جديدة", doing:"قيد التنفيذ", followup:"متابعة", done:"مكتملة", paused:"مؤجلة", canceled:"ملغاة" };
const TYPE_LABEL   = { task:"مهمة", meeting:"اجتماع", appointment:"موعد" };
const THEME_LABELS = { light_sky:"سماوي فاتح", light_mint:"أخضر فاتح", light_sand:"رملي فاتح" };
const INITIAL_FORM = { title:"", type:"task", taskName:"", dueDate:"", dueTime:"08:00", status:"todo", notes:"", followups:[] };
const FOLLOWUP_START = "[[FOLLOWUPS_JSON_START]]";
const FOLLOWUP_END = "[[FOLLOWUPS_JSON_END]]";
const FOLLOWUP_LABELS = ["الأولى","الثانية","الثالثة","الرابعة","الخامسة","السادسة","السابعة","الثامنة","التاسعة","العاشرة"];
 
// ─── Helpers ─────────────────────────────────────────────────────────────────
function timestampToMillis(value) {
  if (!value) return Date.now();
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0)
    return numeric > 1000000000000 ? numeric : numeric * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Date.now() : parsed;
}
function timeToHHMM(value) { if (!value) return ""; return String(value).slice(0, 5); }
function getFollowupLabel(index) { return `المتابعة ${FOLLOWUP_LABELS[index] || index+1}`; }
function normalizeFollowups(value) {
  const arr = Array.isArray(value) ? value : [];
  return arr
    .map((item, index) => ({
      label: item?.label || getFollowupLabel(index),
      date: String(item?.date || "").trim(),
      action: String(item?.action || "").trim(),
    }))
    .filter(item => item.date || item.action)
    .map((item, index) => ({ ...item, label: item.label || getFollowupLabel(index) }));
}
function parseFollowupNotes(rawNotes) {
  const original = String(rawNotes || "");
  const startIndex = original.indexOf(FOLLOWUP_START);
  const endIndex = original.indexOf(FOLLOWUP_END);
  if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
    return { notes: original, followups: [] };
  }
  const before = original.slice(0, startIndex).trim();
  const after = original.slice(endIndex + FOLLOWUP_END.length).trim();
  const jsonText = original.slice(startIndex + FOLLOWUP_START.length, endIndex).trim();
  let followups = [];
  try {
    followups = normalizeFollowups(JSON.parse(jsonText));
  } catch (error) {
    followups = [];
  }
  return { notes: [before, after].filter(Boolean).join("\n").trim(), followups };
}
function buildNotesWithFollowups(notes, followups) {
  const cleanNotes = String(notes || "").trim();
  const normalizedFollowups = normalizeFollowups(followups);
  if (!normalizedFollowups.length) return cleanNotes;
  return `${cleanNotes}${cleanNotes ? "\n\n" : ""}${FOLLOWUP_START}
${JSON.stringify(normalizedFollowups)}
${FOLLOWUP_END}`;
}
function formatFollowups(followups) {
  const normalized = normalizeFollowups(followups);
  if (!normalized.length) return "";
  return normalized.map((item, index) => `${item.label || getFollowupLabel(index)} - ${item.date || "بدون تاريخ"} - ${item.action || "بدون إجراء"}`).join("\n");
}
function parseFollowupsText(value) {
  const text = String(value || "").trim();
  if (!text) return [];
  return normalizeFollowups(text.split(/\r?\n/).map((line, index) => {
    const parts = line.split(" - ");
    if (parts.length >= 3) {
      return {
        label: parts[0].trim() || getFollowupLabel(index),
        date: parts[1].trim() === "بدون تاريخ" ? "" : parts[1].trim(),
        action: parts.slice(2).join(" - ").trim() === "بدون إجراء" ? "" : parts.slice(2).join(" - ").trim(),
      };
    }
    return { label:getFollowupLabel(index), date:"", action:line.trim() };
  }));
}
function getLatestFollowup(task) {
  const followups = normalizeFollowups(task?.followups);
  return followups[followups.length - 1] || null;
}
function dbRowToTask(row) {
  const parsedNotes = parseFollowupNotes(row.notes || "");
  return {
    id: row.id,
    title: row.title || "",
    type: normalizeType(row.type),
    taskName: row.task_name || "",
    dueDate: row.due_date || "",
    dueTime: timeToHHMM(row.due_time),
    status: normalizeStatus(row.status),
    notes: parsedNotes.notes,
    followups: parsedNotes.followups,
    sourceLocalId: row.source_local_id || "",
    createdAt: timestampToMillis(row.created_at),
    updatedAt: timestampToMillis(row.updated_at),
  };
}
function taskToDbRow(task, userId, includeTimestamps = false) {
  const now = new Date().toISOString();
  const row = {
    user_id: userId,
    title: task.title?.trim() || "بدون عنوان",
    type: normalizeType(task.type),
    task_name: normalizeType(task.type) === "task" ? task.taskName || null : null,
    status: normalizeStatus(task.status),
    due_date: task.dueDate || null,
    due_time: task.dueTime || null,
    notes: buildNotesWithFollowups(task.notes, task.followups) || null,
    source_local_id: task.sourceLocalId || task.id || String(Date.now()),
    updated_at: includeTimestamps ? new Date(task.updatedAt || Date.now()).toISOString() : now,
  };
  if (includeTimestamps) row.created_at = new Date(task.createdAt || Date.now()).toISOString();
  return row;
}
function normalizeType(value) {
  const map = { task:"task","مهمة":"task", meeting:"meeting","اجتماع":"meeting", appointment:"appointment","موعد":"appointment" };
  return map[String(value||"").trim()] || "task";
}
function normalizeStatus(value) {
  const map = { todo:"todo","جديدة":"todo", doing:"doing","قيد التنفيذ":"doing", followup:"followup","متابعة":"followup", done:"done","مكتملة":"done", paused:"paused","مؤجلة":"paused", canceled:"canceled","ملغاة":"canceled" };
  return map[String(value||"").trim()] || "todo";
}
function parseImportedTimestamp(value) {
  if (!value) return Date.now();
  const num = Number(value);
  if (!Number.isNaN(num) && num > 0) return num;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? Date.now() : d.getTime();
}
function normalizeImportedTask(row, index) {
  const type = normalizeType(row.type ?? row["النوع"]);
  const parsedImportedNotes = parseFollowupNotes(String(row.notes ?? row["الملاحظات"] ?? "").trim());
  const importedFollowups = parseFollowupsText(row.followups ?? row["تفاصيل المتابعة"]);
  return {
    id: String(row.id ?? row["المعرف"] ?? `${Date.now()}-${index}`),
    title: String(row.title ?? row["عنوان المهمة"] ?? row["العنوان"] ?? "").trim(),
    type,
    taskName: type==="task" ? String(row.taskName ?? row["اسم المهمة"] ?? row["أنواع المهمة"] ?? "").trim() : "",
    dueDate: String(row.dueDate ?? row["التاريخ"] ?? "").trim(),
    dueTime: String(row.dueTime ?? row["الوقت"] ?? "").trim(),
    status: normalizeStatus(row.status ?? row["الحالة"]),
    notes: parsedImportedNotes.notes,
    followups: importedFollowups.length ? importedFollowups : parsedImportedNotes.followups,
    createdAt: parseImportedTimestamp(row.createdAt ?? row["createdAt"] ?? row["وقت الإنشاء الخام"]),
    updatedAt: parseImportedTimestamp(row.updatedAt ?? row["updatedAt"] ?? row["وقت التحديث الخام"]),
  };
}
function toISODate(date) {
  const y = date.getFullYear();
  const m = `${date.getMonth()+1}`.padStart(2,"0");
  const d = `${date.getDate()}`.padStart(2,"0");
  return `${y}-${m}-${d}`;
}
function parseDateTime(task) {
  if (!task?.dueDate) return null;
  const date = new Date(`${task.dueDate}T${task.dueTime||"00:00"}`);
  return Number.isNaN(date.getTime()) ? null : date;
}
function isScheduleItem(task) { return task.type==="meeting" || task.type==="appointment"; }
function isOverdue(task) {
  if (task.status==="done" || task.status==="canceled") return false;
  const due = parseDateTime(task);
  if (!due) return false;
  return due.getTime() < Date.now();
}
function getStatusClass(status) {
  if (status==="done") return "good";
  if (status==="doing") return "info";
  if (status==="followup") return "warn";
  if (status==="paused") return "warn";
  if (status==="canceled") return "bad";
  return "";
}
function formatDateTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ar",{year:"numeric",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}).format(d);
}

// دالة مضافة لحساب الوقت المتبقي
function getRemainingTimeText(dateObj) {
  if (!dateObj) return "";
  const diff = dateObj.getTime() - Date.now();
  if (diff <= 0) return "حان الموعد";
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0 && mins > 0) return `متبقي ${hours} ساعة و ${mins} دقيقة`;
  if (hours > 0) return `متبقي ${hours} ساعة`;
  return `متبقي ${mins} دقيقة`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Dash({ currentUser, onLogout }) {
  const [view, setView]                   = useState("tasks");
  const [theme, setTheme]                 = useState(localStorage.getItem("dash_theme")||"light_sky");
  const [tasks, setTasks]                 = useState([]);
  const [loadingTasks, setLoadingTasks]   = useState(true);
  const [dataMessage, setDataMessage]     = useState({type:"",text:""});
  const [taskForm, setTaskForm]           = useState(INITIAL_FORM);
  const [tasksStatusFilter, setTasksStatusFilter] = useState("all");
  const [reportsQuery, setReportsQuery]   = useState("");
  const [reportsStatus, setReportsStatus] = useState("all");
  const [reportsType, setReportsType]     = useState("all");
  const [fromDate, setFromDate]           = useState("");
  const [toDate, setToDate]               = useState("");
  const [selectedDay, setSelectedDay]     = useState(toISODate(new Date()));
  const [editingItem, setEditingItem]     = useState(null);
  const [editForm, setEditForm]           = useState(INITIAL_FORM);
  const [editFollowupDraft, setEditFollowupDraft] = useState({date:"",action:""});
  const [followupOnlyMode, setFollowupOnlyMode] = useState(false);
  const fileInputRef = useRef(null);
  const [backupMessage, setBackupMessage] = useState({type:"",text:""});
 
  // Theme
  useEffect(() => {
    localStorage.setItem("dash_theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);
 
  // ── Load tasks from Firebase ──────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    const loadTasks = async () => {
      try {
        setLoadingTasks(true);
        setDataMessage({type:"",text:""});
        const user = auth.currentUser;
        const activeUserId = user?.uid;
        if (!activeUserId) {
          if (isMounted) {
            setTasks([]);
            setDataMessage({type:"error",text:"لم يتم التعرف على المستخدم الحالي. سجّل الخروج ثم ادخل مرة أخرى."});
            setLoadingTasks(false);
          }
          return;
        }
        const { data, error } = await listTasks();
        if (!isMounted) return;
        if (error) {
          console.error("Firebase fetch error:", error);
          setDataMessage({type:"error",text:`تعذر تحميل البيانات: ${error.message}`});
          setTasks([]);
        } else {
          setTasks((data||[]).map(dbRowToTask));
        }
      } catch(err) {
        if (isMounted) {
          console.error("Unexpected error:", err);
          setDataMessage({type:"error",text:"حدث خطأ غير متوقع أثناء تحميل البيانات."});
        }
      } finally {
        if (isMounted) setLoadingTasks(false);
      }
    };
    loadTasks();
    return () => { isMounted = false; };
  }, [currentUser?.id]);
 
  // ── Computed ──────────────────────────────────────────────────────────────
  const scheduleItems  = useMemo(() => tasks.filter(isScheduleItem), [tasks]);
  const taskOnlyItems  = useMemo(() => tasks.filter(t => t.type==="task"), [tasks]);
  const selectedDayItems = useMemo(() => scheduleItems
    .filter(t => t.dueDate===selectedDay)
    .sort((a,b)=>(a.dueTime||"23:59").localeCompare(b.dueTime||"23:59")), [scheduleItems,selectedDay]);
  const filteredTasks  = useMemo(() => {
    return taskOnlyItems.filter(t => {
      if (tasksStatusFilter==="all") return true;
      if (tasksStatusFilter==="overdue") return isOverdue(t);
      return t.status===tasksStatusFilter;
    });
  }, [taskOnlyItems, tasksStatusFilter]);
  const taskStats = useMemo(() => ({
    total: taskOnlyItems.length,
    done:  taskOnlyItems.filter(t=>t.status==="done").length,
    doing: taskOnlyItems.filter(t=>t.status==="doing").length,
    followup: taskOnlyItems.filter(t=>t.status==="followup").length,
    overdue: taskOnlyItems.filter(t=>isOverdue(t)).length,
  }), [taskOnlyItems]);
  const reportRows = useMemo(() => {
    return tasks.filter(t => {
      if (reportsType!=="all" && t.type!==reportsType) return false;
      if (reportsStatus!=="all" && t.status!==reportsStatus) return false;
      if (fromDate && t.dueDate && t.dueDate<fromDate) return false;
      if (toDate   && t.dueDate && t.dueDate>toDate)   return false;
      if (reportsQuery) {
        const q = reportsQuery.toLowerCase();
        return t.title.toLowerCase().includes(q)||(t.notes||"").toLowerCase().includes(q);
      }
      return true;
    });
  }, [tasks, reportsType, reportsStatus, fromDate, toDate, reportsQuery]);
  const upcomingItems = useMemo(() => {
    const now=Date.now(); const week=7*24*60*60*1000;
    return tasks.filter(t=>{
      if (t.status==="done"||t.status==="canceled") return false;
      const d=parseDateTime(t); return d&&d.getTime()>now&&d.getTime()-now<=week;
    }).sort((a,b)=>(parseDateTime(a)?.getTime()||0)-(parseDateTime(b)?.getTime()||0)).slice(0,10);
  }, [tasks]);

  // تعديل soonItems ليشمل نطاق الـ 24 ساعة ويستبعد المهام المكتملة/الملغاة مع الترتيب
  const soonItems = useMemo(() => {
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return tasks
      .filter(t => {
        if (t.status === "done" || t.status === "canceled") return false;
        const d = parseDateTime(t);
        if (!d) return false;
        const diff = d.getTime() - now;
        return diff > 0 && diff <= twentyFourHours;
      })
      .sort((a, b) => {
        const timeA = parseDateTime(a)?.getTime() || 0;
        const timeB = parseDateTime(b)?.getTime() || 0;
        return timeA - timeB;
      });
  }, [tasks]);

  const statusChartData = useMemo(() => {
    const colors={todo:"#6b7280",doing:"#3b82f6",followup:"#f97316",done:"#22c55e",paused:"#f59e0b",canceled:"#ef4444"};
    return Object.entries(STATUS_LABEL).map(([key,label])=>({key,label,value:reportRows.filter(t=>t.status===key).length,color:colors[key]})).filter(d=>d.value>0);
  }, [reportRows]);
  const typeChartData = useMemo(() => {
    const colors={task:"#6366f1",meeting:"#0ea5e9",appointment:"#f59e0b"};
    return Object.entries(TYPE_LABEL).map(([key,label])=>({key,label,value:reportRows.filter(t=>t.type===key).length,color:colors[key]}));
  }, [reportRows]);
  const pieTotal = useMemo(() => statusChartData.reduce((s,d)=>s+d.value,0), [statusChartData]);
  const maxTypeChartValue = useMemo(() => Math.max(...typeChartData.map(d=>d.value),1), [typeChartData]);
  const pieGradient = useMemo(() => {
    if (!statusChartData.length) return "#e5e7eb";
    let deg=0;
    const parts=statusChartData.map(d=>{ const pct=pieTotal?(d.value/pieTotal)*360:0; const seg=`${d.color} ${deg}deg ${deg+pct}deg`; deg+=pct; return seg; });
    return `conic-gradient(${parts.join(", ")})`;
  }, [statusChartData, pieTotal]);
 
  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleTaskFormChange = (field, value) => setTaskForm(p=>({...p,[field]:value}));
  const handleEditFormChange = (field, value) => setEditForm(p=>({...p,[field]:value}));
 
  const handleAddTask = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    const uid = user?.uid;
    if (!uid) { setDataMessage({type:"error",text:"لم يتم التعرف على المستخدم."}); return; }
    const row = taskToDbRow(taskForm, uid);
    const { data, error } = await createTask(row);
    if (error) { console.error(error); setDataMessage({type:"error",text:`تعذر إضافة المهمة: ${error.message}`}); return; }
    setTasks(p=>[dbRowToTask(data),...p]);
    setTaskForm(INITIAL_FORM);
    setDataMessage({type:"success",text:"تمت إضافة المهمة بنجاح."});
  };
 
  const updateTaskStatus = async (id, status) => {
    const { data, error } = await editTask(id, {status, updated_at:new Date().toISOString()});
    if (error) { console.error(error); setDataMessage({type:"error",text:"تعذر تحديث الحالة."}); return; }
    setTasks(p=>p.map(t=>String(t.id)===String(id)?dbRowToTask(data):t));
    setDataMessage({type:"success",text:"تم تحديث الحالة بنجاح."});
  };
 
  const deleteTask = async (id) => {
    if (!window.confirm("هل تريد حذف هذا السجل؟")) return;
    const { error } = await removeTask(id);
    if (error) { console.error(error); setDataMessage({type:"error",text:"تعذر حذف السجل."}); return; }
    setTasks(p=>p.filter(t=>String(t.id)!==String(id)));
    setDataMessage({type:"success",text:"تم حذف السجل بنجاح."});
  };
 
  const openEditModal = (task) => {
    setFollowupOnlyMode(false);
    setEditingItem(task);
    setEditForm({title:task.title,type:task.type,taskName:task.taskName,dueDate:task.dueDate,dueTime:task.dueTime,status:task.status,notes:task.notes,followups:normalizeFollowups(task.followups)});
    setEditFollowupDraft({date:task.dueDate||"",action:""});
  };
  const openFollowupModal = (task) => {
    setFollowupOnlyMode(true);
    setEditingItem(task);
    setEditForm({title:task.title,type:task.type,taskName:task.taskName,dueDate:task.dueDate,dueTime:task.dueTime,status:task.status,notes:task.notes,followups:normalizeFollowups(task.followups)});
    setEditFollowupDraft({date:toISODate(new Date()),action:""});
  };
  const closeEditModal = () => { setEditingItem(null); setEditForm(INITIAL_FORM); setEditFollowupDraft({date:"",action:""}); setFollowupOnlyMode(false); };

  const handleAddFollowupDetail = () => {
    const date = editFollowupDraft.date || editForm.dueDate;
    const action = editFollowupDraft.action.trim();
    if (!date || !action) {
      window.alert("يرجى إدخال تاريخ المتابعة والإجراء المطلوب.");
      return;
    }
    setEditForm(prev => {
      const currentFollowups = normalizeFollowups(prev.followups);
      const nextFollowup = { label:getFollowupLabel(currentFollowups.length), date, action };
      return {
        ...prev,
        followups:[...currentFollowups,nextFollowup],
      };
    });
    setEditFollowupDraft({date:"",action:""});
  };

  const handleRemoveFollowupDetail = (index) => {
    setEditForm(prev => {
      const nextFollowups = normalizeFollowups(prev.followups).filter((_, i) => i !== index)
        .map((item, i) => ({...item,label:getFollowupLabel(i)}));
      return {...prev, followups:nextFollowups};
    });
  };
 
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    const user = auth.currentUser;
    const uid = user?.uid;
    const row = taskToDbRow({...editForm,id:editingItem.id,sourceLocalId:editingItem.sourceLocalId}, uid);
    const { data, error } = await editTask(editingItem.id, row);
    if (error) { console.error(error); setDataMessage({type:"error",text:`تعذر حفظ التعديلات: ${error.message}`}); return; }
    setTasks(p=>p.map(t=>String(t.id)===String(editingItem.id)?dbRowToTask(data):t));
    setDataMessage({type:"success",text:"تم حفظ التعديلات بنجاح."});
    closeEditModal();
  };
 
  const handleExportExcel = () => {
    const rows = reportRows.map(t=>({
      "المعرف":t.id,"عنوان المهمة":t.title,"النوع":TYPE_LABEL[t.type]||t.type,
      "اسم المهمة":t.taskName||"","الحالة":STATUS_LABEL[t.status]||t.status,
      "التاريخ":t.dueDate||"","الوقت":t.dueTime||"","الملاحظات":t.notes||"","تفاصيل المتابعة":formatFollowups(t.followups),
      "وقت الإنشاء الخام":t.createdAt,"وقت التحديث الخام":t.updatedAt,
    }));
    const ws=XLSX.utils.json_to_sheet(rows); const wb=XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb,ws,"المهام");
    XLSX.writeFile(wb,`tasks_backup_${toISODate(new Date())}.xlsx`);
    setBackupMessage({type:"success",text:"تم تصدير البيانات الحالية حسب نتيجة البحث بنجاح."});
  };
 
  const handleImportExcel = async (e) => {
    const file=e.target.files?.[0]; if (!file) return;
    const user = auth.currentUser;
    const uid = user?.uid;
    if (!uid) { setBackupMessage({type:"error",text:"لم يتم التعرف على المستخدم."}); return; }
    const reader=new FileReader();
    reader.onload = async (ev) => {
      try {
        const wb=XLSX.read(ev.target.result,{type:"array"});
        const ws=wb.Sheets[wb.SheetNames[0]];
        const raw=XLSX.utils.sheet_to_json(ws,{defval:""});
        const imported=raw.map((row,i)=>normalizeImportedTask(row,i));
        const rows=imported.map(t=>taskToDbRow(t,uid,true));
        const { error }=await importTasks(rows);
        if (error) { console.error(error); setBackupMessage({type:"error",text:`تعذر الاستيراد: ${error.message}`}); return; }
        const { data, error:fe }=await listTasks();
        if (fe) { setBackupMessage({type:"error",text:"تم حفظ البيانات ولكن تعذر تحديث العرض. أعد تحميل الصفحة."}); return; }
        setTasks((data||[]).map(dbRowToTask));
        setBackupMessage({type:"success",text:`تم استيراد ${imported.length} سجل بنجاح.`});
      } catch(err) { setBackupMessage({type:"error",text:"تعذر قراءة الملف."}); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value="";
  };
 
  const clearReportFilters = () => { setReportsQuery(""); setReportsStatus("all"); setReportsType("all"); setFromDate(""); setToDate(""); };
  const handleLogout = () => { if (typeof onLogout==="function") { onLogout(); return; } window.location.reload(); };
 
  // ── Layout ────────────────────────────────────────────────────────────────
  const actionRowStyle   = {display:"flex",alignItems:"center",gap:"8px",flexWrap:"nowrap",whiteSpace:"nowrap"};
  const actionButtonStyle = {whiteSpace:"nowrap",minWidth:"72px"};
  const sectionTitleStyle = {color:"var(--primary-2)",fontWeight:900};
  const modalOverlayStyle = {position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",zIndex:9999};
  const modalBoxStyle     = {width:"100%",maxWidth:"820px",maxHeight:"90vh",overflowY:"auto",background:"#fff",borderRadius:"20px",padding:"22px",boxShadow:"0 20px 50px rgba(0,0,0,0.18)",direction:"rtl"};
  const modalHeaderStyle  = {display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px",marginBottom:"16px",flexWrap:"wrap"};
  const messageStyle = (type) => ({
    marginTop:"10px",padding:"12px 14px",borderRadius:"14px",fontSize:"14px",fontWeight:"700",border:"1px solid",
    background:type==="success"?"#ecfdf3":"#fef2f2",
    color:type==="success"?"#166534":"#b91c1c",
    borderColor:type==="success"?"#bbf7d0":"#fecaca",
  });
 
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      <header>
        <div className="titleSection">
          <h1 className="mainTitle" style={{ fontSize: "42px", color: "var(--primary)", fontWeight: 900, letterSpacing: "0.6px" }}>نِظَامُ مُتَابَعَةِ الأَعْمَالِ وَالمَهَامِّ</h1>
          <div className="authBar" style={{ justifyContent: "flex-end" }}>
            <button className="btn danger" type="button" onClick={handleLogout}>🚪 تسجيل الخروج</button>
          </div>
        </div>
        <div className="tabsSection">
          <div className="tabsWrap">
            <nav aria-label="التنقل">
              <button className={`tab ${view==="tasks"?"active":""}`} onClick={()=>setView("tasks")} type="button">✅ المهام</button>
              <button className={`tab ${view==="calendar"?"active":""}`} onClick={()=>setView("calendar")} type="button">🗓️ المواعيد والاجتماعات</button>
              <button className={`tab ${view==="reports"?"active":""}`} onClick={()=>setView("reports")} type="button">📈 التقارير</button>
              <button className={`tab ${view==="notifications"?"active":""}`} onClick={()=>setView("notifications")} type="button">🔔 التنبيهات</button>
              <button className={`tab ${view==="themes"?"active":""}`} onClick={()=>setView("themes")} type="button">🎨 الثيمات</button>
            </nav>
          </div>
        </div>
      </header>
 
      <main>
        {loadingTasks && <section className="card"><h2><span style={sectionTitleStyle}>⏳ جاري تحميل البيانات من Firebase...</span></h2></section>}
        {dataMessage.text && <div style={messageStyle(dataMessage.type)}>{dataMessage.text}</div>}
 
        {/* ── المهام ── */}
        {!loadingTasks && view==="tasks" && (
          <section className="card">
            <h2><span style={sectionTitleStyle}>✅ إدارة المهام</span></h2>
            <div className="card mt10">
              <h2><span style={sectionTitleStyle}>📊 مؤشرات المهام</span></h2>
              <div className="kpis">
                <div className="kpi"><span className="label">إجمالي المهام</span><span className="value">{taskStats.total}</span></div>
                <div className="kpi"><span className="label">مكتملة</span><span className="value">{taskStats.done}</span></div>
                <div className="kpi"><span className="label">قيد التنفيذ</span><span className="value">{taskStats.doing}</span></div>
                <div className="kpi"><span className="label">المتابعة</span><span className="value">{taskStats.followup}</span></div>
                <div className="kpi"><span className="label">متأخرة</span><span className="value">{taskStats.overdue}</span></div>
              </div>
            </div>
            <div className="card mt10">
              <h2><span style={sectionTitleStyle}>➕ إضافة مهمة</span></h2>
              <form className="form grid-3" onSubmit={handleAddTask}>
                <label>عنوان المهمة *<input value={taskForm.title} onChange={e=>handleTaskFormChange("title",e.target.value)} placeholder="مثال: متابعة مراسلات البريد" required /></label>
                <label>النوع
                  <select value={taskForm.type} onChange={e=>{handleTaskFormChange("type",e.target.value);if(e.target.value!=="task")handleTaskFormChange("taskName","");}}>
                    <option value="task">مهمة</option><option value="meeting">اجتماع</option><option value="appointment">موعد</option>
                  </select>
                </label>
                {taskForm.type==="task"&&(
                  <label>أنواع المهمة
                    <select value={taskForm.taskName} onChange={e=>handleTaskFormChange("taskName",e.target.value)}>
                      <option value="">اختر نوع المهمة</option>
                      {TASK_NAMES.map(item=><option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                )}
                <label>التاريخ (استحقاق) *<input type="date" value={taskForm.dueDate} onChange={e=>handleTaskFormChange("dueDate",e.target.value)} required /></label>
                <label>الوقت (للتنبيه) ⏰<input type="time" step="60" value={taskForm.dueTime} onChange={e=>handleTaskFormChange("dueTime",e.target.value)} /></label>
                <label>الحالة
                  <select value={taskForm.status} onChange={e=>handleTaskFormChange("status",e.target.value)}>
                    <option value="todo">جديدة</option><option value="doing">قيد التنفيذ</option><option value="followup">متابعة</option><option value="done">مكتملة</option><option value="paused">مؤجلة</option><option value="canceled">ملغاة</option>
                  </select>
                </label>
                <label className="full">ملاحظات<textarea value={taskForm.notes} onChange={e=>handleTaskFormChange("notes",e.target.value)} placeholder="أي تفاصيل إضافية…" /></label>
                <div className="row full mt10">
                  <div className="left"><button className="btn primary" type="submit">💾 حفظ المهمة</button></div>
                  <div className="right"><span className="small">التنبيه يعمل فقط إذا حددت وقتًا.</span></div>
                </div>
              </form>
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>📌 ملخص سريع</span></h2>
              <div className="small">لديك {taskStats.total} مهمة، منها {taskStats.done} مكتملة، {taskStats.doing} قيد التنفيذ، {taskStats.followup} للمتابعة، و{taskStats.overdue} متأخرة.</div>
              <div className="divider"></div>
              <div className="small"><span className="ok">مكتملة</span> ✅ | <span className="warnText">قريبة</span> ⏳ | <span className="badText">متأخرة</span> ⚠️</div>
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>📋 سجل المهام</span></h2>
              <div className="row mt10">
                <div className="left">
                  <select className="filterSelect" value={tasksStatusFilter} onChange={e=>setTasksStatusFilter(e.target.value)}>
                    <option value="all">الكل</option><option value="done">مكتملة</option><option value="overdue">متأخرة</option><option value="doing">قيد التنفيذ</option><option value="followup">متابعة</option>
                  </select>
                </div>
              </div>
              <div className="divider"></div>
              <div className="list">
                {filteredTasks.length ? filteredTasks.map(task=>(
                  <div className="item taskSimpleItem" key={task.id}>
                    <div className="taskSimpleMain">
                      <h3>{task.title}</h3>
                      <div className="meta">
                        <span className="badge">{TYPE_LABEL[task.type]}</span>
                        {task.taskName&&<span className="badge">{task.taskName}</span>}
                        <span className={`badge ${getStatusClass(task.status)}`}>{STATUS_LABEL[task.status]}</span>
                      </div>
                      <div className="small muted">{task.dueDate} {task.dueTime||""}</div>
                      {task.status==="followup" && getLatestFollowup(task) && (
                        <div className="small" style={{marginTop:"6px",color:"#92400e",fontWeight:800}}>آخر متابعة: {getLatestFollowup(task).label} - {getLatestFollowup(task).date} - {getLatestFollowup(task).action}</div>
                      )}
                    </div>
                    <div className="taskSimpleStatus">
                      {isOverdue(task)?<span className="badge bad">متأخرة</span>:task.status==="done"?<span className="badge good">مكتملة</span>:<span className="badge info">نشطة</span>}
                    </div>
                    <div className="itemActions" style={actionRowStyle}>
                      {task.status==="followup" && (
                        <button className="btn" type="button" style={actionButtonStyle} onClick={()=>openFollowupModal(task)}>متابعة</button>
                      )}
                      <button className="btn" type="button" style={actionButtonStyle} onClick={()=>openEditModal(task)}>تعديل</button>
                      <button className="btn" type="button" style={actionButtonStyle} onClick={()=>updateTaskStatus(task.id,"done")}>إنهاء</button>
                      <button className="btn danger" type="button" style={actionButtonStyle} onClick={()=>deleteTask(task.id)}>حذف</button>
                    </div>
                  </div>
                )) : <p className="small muted">لا توجد مهام مطابقة للفلتر الحالي.</p>}
              </div>
            </div>
          </section>
        )}
 
        {/* ── المواعيد والاجتماعات ── */}
        {!loadingTasks && view==="calendar" && (
          <section className="card">
            <h2><span style={sectionTitleStyle}>🗓️ المواعيد والاجتماعات</span></h2>
            <div className="kpis">
              <div className="kpi"><span className="label">إجمالي المواعيد</span><span className="value">{scheduleItems.filter(t=>t.type==="appointment").length}</span></div>
              <div className="kpi"><span className="label">إجمالي الاجتماعات</span><span className="value">{scheduleItems.filter(t=>t.type==="meeting").length}</span></div>
              <div className="kpi"><span className="label">اليوم المحدد</span><span className="value">{selectedDayItems.length}</span></div>
              <div className="kpi"><span className="label">هذا الشهر</span><span className="value">{scheduleItems.filter(t=>{const d=parseDateTime(t);const now=new Date();return d&&d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();}).length}</span></div>
            </div>
            <div className="card mt10">
              <h2><span style={sectionTitleStyle}>📅 تحديد اليوم</span></h2>
              <div className="form grid-2">
                <label>اختر التاريخ لعرض تفاصيله
                  <input type="date" value={selectedDay} onChange={e=>setSelectedDay(e.target.value)} />
                </label>
                <div style={{display:"flex",alignItems:"flex-end"}}>
                  <button className="btn" type="button" onClick={()=>setSelectedDay(toISODate(new Date()))}>🎯 عرض اليوم</button>
                </div>
              </div>
            </div>
            <div className="card mt12">
                <h2><span style={sectionTitleStyle}>📅 تفاصيل اليوم المحدد</span></h2>
                <div className="small muted mb10">اليوم المحدد: {selectedDay}</div>
                <div className="list">
                  {selectedDayItems.length ? selectedDayItems.map(task=>(
                    <div className="item" key={task.id} style={{alignItems:"stretch",flexDirection:"column"}}>
                      <div className="row">
                        <div className="taskSimpleMain">
                        <h3>{task.title}</h3>
                        <div className="meta">
                          <span className="badge">{TYPE_LABEL[task.type]}</span>
                          <span className={`badge ${getStatusClass(task.status)}`}>{STATUS_LABEL[task.status]}</span>
                          {isOverdue(task)&&<span className="badge bad">متأخرة</span>}
                        </div>
                        </div>
                        <div className="itemActions" style={actionRowStyle}>
                          <button className="btn" type="button" style={actionButtonStyle} onClick={()=>openEditModal(task)}>تعديل</button>
                          <button className="btn" type="button" style={actionButtonStyle} onClick={()=>updateTaskStatus(task.id,"done")}>إنهاء</button>
                          <button className="btn danger" type="button" style={actionButtonStyle} onClick={()=>deleteTask(task.id)}>حذف</button>
                        </div>
                      </div>
                      <div className="divider"></div>
                      <div className="grid-3">
                        <div><div className="small muted">التاريخ</div><strong>{task.dueDate||"—"}</strong></div>
                        <div><div className="small muted">الوقت</div><strong>{task.dueTime||"غير محدد"}</strong></div>
                        <div><div className="small muted">الحالة التشغيلية</div><strong>{task.status==="done"?"مكتملة":isOverdue(task)?"متأخرة":"نشطة"}</strong></div>
                      </div>
                      <div className="divider"></div>
                      <div>
                        <div className="small muted">الملاحظات</div>
                        <div className="small" style={{marginTop:"5px",whiteSpace:"pre-wrap"}}>{task.notes||"لا توجد ملاحظات مسجلة."}</div>
                      </div>
                      <div className="divider"></div>
                      <div>
                        <div className="small muted mb10">تفاصيل المتابعة</div>
                        {normalizeFollowups(task.followups).length ? (
                          <div className="list">
                            {normalizeFollowups(task.followups).map((followup,index)=>(
                              <div className="meta" key={`${task.id}-followup-${index}`}>
                                <span className="badge warn">{followup.label||getFollowupLabel(index)}</span>
                                <span className="badge">{followup.date||"بدون تاريخ"}</span>
                                <span className="badge info">{followup.action||"بدون إجراء"}</span>
                              </div>
                            ))}
                          </div>
                        ) : <div className="small">لا توجد متابعات مسجلة.</div>}
                      </div>
                      <div className="divider"></div>
                      <div className="grid-2">
                        <div><div className="small muted">تاريخ إنشاء السجل</div><span className="small">{formatDateTime(task.createdAt)}</span></div>
                        <div><div className="small muted">آخر تحديث</div><span className="small">{formatDateTime(task.updatedAt)}</span></div>
                      </div>
                    </div>
                  )) : <p className="small muted">لا توجد مواعيد أو اجتماعات في هذا اليوم.</p>}
                </div>
            </div>
          </section>
        )}
 
        {/* ── التقارير ── */}
        {!loadingTasks && view==="reports" && (
          <section className="card">
            <h2><span style={sectionTitleStyle}>📈 التقارير</span></h2>
            <div className="kpis">
              <div className="kpi"><span className="label">إجمالي المهام</span><span className="value">{tasks.length}</span><span className="hint">كل العناصر</span></div>
              <div className="kpi"><span className="label">المكتملة</span><span className="value">{tasks.filter(t=>t.status==="done").length}</span><span className="hint">تم إنجازها</span></div>
              <div className="kpi"><span className="label">المتأخرة</span><span className="value">{tasks.filter(t=>isOverdue(t)).length}</span><span className="hint">بحاجة متابعة</span></div>
              <div className="kpi"><span className="label">المواعيد والاجتماعات</span><span className="value">{scheduleItems.length}</span><span className="hint">من إجمالي العناصر</span></div>
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>🔎 فلترة وبحث</span></h2>
              <div className="form grid-2">
                <label>بحث<input value={reportsQuery} onChange={e=>setReportsQuery(e.target.value)} placeholder="ابحث بالعنوان/الملاحظات…" /></label>
                <label>الحالة<select value={reportsStatus} onChange={e=>setReportsStatus(e.target.value)}>
                  <option value="all">الكل</option><option value="todo">جديدة</option><option value="doing">قيد التنفيذ</option><option value="followup">متابعة</option><option value="done">مكتملة</option><option value="paused">مؤجلة</option><option value="canceled">ملغاة</option>
                </select></label>
                <label>النوع<select value={reportsType} onChange={e=>setReportsType(e.target.value)}>
                  <option value="all">الكل</option><option value="task">مهمة</option><option value="meeting">اجتماع</option><option value="appointment">موعد</option>
                </select></label>
                <label>من تاريخ<input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} /></label>
                <label>إلى تاريخ<input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} /></label>
              </div>
              <div className="row mt10">
                <div className="left"><button className="btn" type="button" onClick={clearReportFilters}>🧽 مسح الفلاتر</button></div>
              </div>
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>📑 نتيجة البحث</span></h2>
              <div className="small muted mb10">عدد النتائج: {reportRows.length}</div>
              <div className="reportTableWrap">
                <div className="tableScroller">
                  <table className="reportTable">
                    <thead><tr><th>عنوان المهمة</th><th>اسم المهمة</th><th>النوع</th><th>الحالة</th><th>التاريخ</th><th>الوقت</th><th>الملاحظات</th><th>أضيفت في</th><th>آخر تحديث</th></tr></thead>
                    <tbody>
                      {reportRows.map(task=>(
                        <tr key={task.id}>
                          <td>{task.title}</td><td>{task.taskName||"—"}</td><td>{TYPE_LABEL[task.type]}</td><td>{STATUS_LABEL[task.status]}</td>
                          <td>{task.dueDate||"—"}</td><td>{task.dueTime||"—"}</td><td className="notesCell">{task.notes||"—"}</td>
                          <td>{formatDateTime(task.createdAt)}</td><td>{formatDateTime(task.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!reportRows.length&&<div className="reportEmpty">لا توجد بيانات مطابقة للفلاتر الحالية.</div>}
              </div>
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>🗂️ إدارة البيانات</span></h2>
              <div className="small muted">يمكنك تصدير نتيجة البحث الحالية إلى Excel أو استيراد ملف Excel لإعادة تحميل السجلات.</div>
              <div className="divider"></div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}} onChange={handleImportExcel} />
              <div className="row"><div className="left">
                <button className="btn" type="button" onClick={handleExportExcel}>⬇️ تصدير البيانات</button>
                <button className="btn" type="button" onClick={()=>fileInputRef.current?.click()}>⬆️ استيراد البيانات</button>
              </div></div>
              {backupMessage.text&&<div style={messageStyle(backupMessage.type)}>{backupMessage.text}</div>}
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>📈 الرسومات حسب نتيجة البحث</span></h2>
              {reportRows.length ? (
                <div style={{display:"grid",gap:"26px",marginTop:"14px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"220px 1fr",gap:"18px",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:"800",marginBottom:"12px",color:"#172033",fontSize:"15px",textAlign:"center"}}>الرسمة الدائرية حسب الحالة</div>
                      <div style={{width:"200px",height:"200px",margin:"0 auto",borderRadius:"50%",background:pieGradient,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 10px 24px rgba(15,23,42,0.08)"}}>
                        <div style={{width:"110px",height:"110px",borderRadius:"50%",background:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",boxShadow:"inset 0 0 0 1px #e5e7eb",fontWeight:"800",color:"#172033"}}>
                          <span style={{fontSize:"13px",color:"#6b7280"}}>الإجمالي</span>
                          <span style={{fontSize:"28px"}}>{reportRows.length}</span>
                        </div>
                      </div>
                    </div>
                    <div style={{display:"grid",gap:"10px"}}>
                      {statusChartData.map(item=>(
                        <div key={item.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px",padding:"10px 12px",borderRadius:"12px",background:"#f8fafc",border:"1px solid #e5e7eb"}}>
                          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                            <span style={{width:"12px",height:"12px",borderRadius:"50%",background:item.color,display:"inline-block"}}></span>
                            <span style={{fontWeight:"700"}}>{item.label}</span>
                          </div>
                          <div style={{fontWeight:"800",color:"#172033"}}>{item.value} ({pieTotal?Math.round((item.value/pieTotal)*100):0}%)</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{borderTop:"1px solid #e5e7eb",paddingTop:"18px"}}>
                    <div style={{fontWeight:"800",marginBottom:"12px",color:"#172033",fontSize:"15px"}}>المخطط العمودي حسب النوع</div>
                    <div style={{display:"grid",gap:"14px"}}>
                      {typeChartData.map(item=>(
                        <div key={item.key} style={{display:"grid",gap:"6px"}}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:"14px",fontWeight:"700"}}>
                            <span>{item.label}</span><span>{item.value}</span>
                          </div>
                          <div style={{height:"18px",borderRadius:"999px",background:"#eef2f7",overflow:"hidden",border:"1px solid #e5e7eb"}}>
                            <div style={{height:"100%",width:`${(item.value/maxTypeChartValue)*100}%`,background:item.color,borderRadius:"999px",transition:"0.3s ease"}}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : <div className="small muted" style={{marginTop:"12px"}}>لا توجد بيانات مطابقة لعرض الرسومات حاليًا.</div>}
            </div>
            <div className="card mt12">
              <h2><span style={sectionTitleStyle}>الأقرب استحقاقًا</span></h2>
              <div className="list">
                {upcomingItems.length ? upcomingItems.map(task=>(
                  <div className="item" key={task.id}>
                    <div>
                      <h3>{task.title}</h3>
                      <div className="meta">
                        <span className="badge">{TYPE_LABEL[task.type]}</span>
                        <span className={`badge ${getStatusClass(task.status)}`}>{STATUS_LABEL[task.status]}</span>
                        <span className="badge info">{task.dueDate} {task.dueTime||""}</span>
                      </div>
                    </div>
                  </div>
                )) : <p className="small muted">لا توجد عناصر قريبة الاستحقاق.</p>}
              </div>
            </div>
          </section>
        )}

        {/* ── التنبيهات ── */}
        {!loadingTasks && view==="notifications" && (
          <section className="card">
            <h2><span style={sectionTitleStyle}>🔔 التنبيهات</span></h2>
            <div className="small">يعرض هذا القسم العناصر والمواعيد التي يتبقى على استحقاقها 24 ساعة أو أقل.</div>
            <div className="divider"></div>
            <div className="row">
              <div className="left">
                <button className="btn info" type="button">🔔 تفعيل التنبيهات</button>
                <button className="btn" type="button">🧪 اختبار</button>
              </div>
            </div>
            <div className="divider"></div>
            <h2><span style={sectionTitleStyle}>قادم خلال 24 ساعة</span></h2>
            <div className="list">
              {soonItems.length ? soonItems.map(task=>(
                <div className="item" key={task.id}>
                  <div>
                    <h3>{task.title}</h3>
                    <div className="meta">
                      <span className="badge">{TYPE_LABEL[task.type]}</span>
                      <span className="badge info">{task.dueDate} {task.dueTime}</span>
                      <span className="badge warn">{getRemainingTimeText(parseDateTime(task))}</span>
                    </div>
                  </div>
                </div>
              )) : <p className="small muted">لا توجد تنبيهات قريبة خلال 24 ساعة حاليًا.</p>}
            </div>
          </section>
        )}
 
        {/* ── الثيمات ── */}
        {!loadingTasks && view==="themes" && (
          <section className="card">
            <h2><span style={sectionTitleStyle}>🎨 الثيمات</span></h2>
            <div className="small muted mb10">اختر ثيمًا فاتحًا للنظام، وسيتم حفظ اختيارك تلقائيًا.</div>
            <div className="themesGrid">
              {Object.keys(THEME_LABELS).map(key=>(
                <button type="button" key={key} className={`themeCard ${theme===key?"active":""}`} onClick={()=>setTheme(key)}>
                  <div className="themeName">{THEME_LABELS[key]}</div>
                </button>
              ))}
            </div>
            <div className="divider"></div>
            <div className="small">الثيم الحالي: <strong>{THEME_LABELS[theme]}</strong></div>
          </section>
        )}
      </main>
 
      {/* ── Modal تعديل ── */}
      {editingItem && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={{margin:0,color:"var(--primary-2)",fontWeight:900}}>{followupOnlyMode ? "🟠 إضافة متابعة" : "✏️ تعديل السجل"}</h2>
              <button className="btn danger" type="button" onClick={closeEditModal}>إغلاق</button>
            </div>
            <form className="form grid-3" onSubmit={handleSaveEdit}>
              {followupOnlyMode && (
                <div className="full" style={{border:"1px solid #e5e7eb",background:"#f8fafc",borderRadius:"16px",padding:"12px"}}>
                  <h3 style={{margin:"0 0 6px",color:"#172033",fontWeight:900}}>{editForm.title}</h3>
                  <div className="small muted">سيتم حفظ المتابعة الجديدة ضمن السجل الحالي دون تغيير حالة المهمة.</div>
                </div>
              )}
              {!followupOnlyMode && (
                <>
              <label>عنوان السجل *<input value={editForm.title} onChange={e=>handleEditFormChange("title",e.target.value)} placeholder="اكتب العنوان" required /></label>
              <label>النوع<select value={editForm.type} onChange={e=>{handleEditFormChange("type",e.target.value);if(e.target.value!=="task")handleEditFormChange("taskName","");}}>
                <option value="task">مهمة</option><option value="meeting">اجتماع</option><option value="appointment">موعد</option>
              </select></label>
              {editForm.type==="task"&&(
                <label>أنواع المهمة<select value={editForm.taskName} onChange={e=>handleEditFormChange("taskName",e.target.value)}>
                  <option value="">اختر نوع المهمة</option>
                  {TASK_NAMES.map(item=><option key={item} value={item}>{item}</option>)}
                </select></label>
              )}
              <label>التاريخ *<input type="date" value={editForm.dueDate} onChange={e=>handleEditFormChange("dueDate",e.target.value)} required /></label>
              <label>الوقت<input type="time" step="60" value={editForm.dueTime} onChange={e=>handleEditFormChange("dueTime",e.target.value)} /></label>
              <label>الحالة<select value={editForm.status} onChange={e=>handleEditFormChange("status",e.target.value)}>
                <option value="todo">جديدة</option><option value="doing">قيد التنفيذ</option><option value="followup">متابعة</option><option value="done">مكتملة</option><option value="paused">مؤجلة</option><option value="canceled">ملغاة</option>
              </select></label>
                </>
              )}
              {editForm.status==="followup" && (
                <div className="full" style={{border:"1px solid #fed7aa",background:"#fff7ed",borderRadius:"16px",padding:"14px",display:"grid",gap:"12px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                    <h3 style={{margin:0,color:"#9a3412",fontWeight:900}}>🟠 تفاصيل المتابعة</h3>
                    <span className="small muted">سيتم حفظ كل متابعة كتفصيل مستقل حسب التاريخ والإجراء دون تغيير حالة السجل.</span>
                  </div>
                  <div className="form grid-2">
                    <label>تاريخ المتابعة<input type="date" value={editFollowupDraft.date} onChange={e=>setEditFollowupDraft(p=>({...p,date:e.target.value}))} /></label>
                    <label>الإجراء المطلوب<input value={editFollowupDraft.action} onChange={e=>setEditFollowupDraft(p=>({...p,action:e.target.value}))} placeholder="مثال: الاتصال بالجهة / إرسال تذكير / انتظار الرد" /></label>
                  </div>
                  <div className="row">
                    <div className="left"><button className="btn" type="button" onClick={handleAddFollowupDetail}>➕ إضافة متابعة</button></div>
                  </div>
                  <div className="list">
                    {normalizeFollowups(editForm.followups).length ? normalizeFollowups(editForm.followups).map((item,index)=>(
                      <div className="item" key={`${item.label}-${index}`} style={{alignItems:"center"}}>
                        <div>
                          <h3 style={{marginBottom:"6px"}}>{item.label}</h3>
                          <div className="meta"><span className="badge warn">{item.date || "بدون تاريخ"}</span><span className="badge info">{item.action || "بدون إجراء"}</span></div>
                        </div>
                        <div className="itemActions" style={actionRowStyle}>
                          <button className="btn danger" type="button" style={actionButtonStyle} onClick={()=>handleRemoveFollowupDetail(index)}>حذف</button>
                        </div>
                      </div>
                    )) : <p className="small muted">لا توجد تفاصيل متابعة محفوظة لهذا السجل حتى الآن.</p>}
                  </div>
                </div>
              )}
              {!followupOnlyMode && (
                <label className="full">الملاحظات<textarea value={editForm.notes} onChange={e=>handleEditFormChange("notes",e.target.value)} placeholder="اكتب الملاحظات" /></label>
              )}
              <div className="row full mt10">
                <div className="left"><button className="btn primary" type="submit">💾 {followupOnlyMode ? "حفظ المتابعة" : "حفظ التعديلات"}</button></div>
                <div className="right"><span className="small muted">{followupOnlyMode ? "ستضاف المتابعة إلى السجل دون تغيير الحالة." : "سيتم تحديث السجل المحدد مباشرة بعد الحفظ."}</span></div>
              </div>
            </form>
          </div>
        </div>
      )}
 
      <footer className="appFooter">
        <span className="footerText">جَمِيعُ الحُقُوقِ مَحْفُوظَةٌ لَدَى / <span className="gold">شَيْمَاءُ الجَابِرِيَّة</span></span>
      </footer>
    </div>
  );
}